import { mat4, ReadonlyMat4, vec3 } from 'gl-matrix';
import { DeformableTransform } from '~/domain/dvf/deformableTransform';
import { dvfDisplacementInto } from '~/domain/dvf/dvfField';
import { Grid } from '~/engine/data/common/Grid';
import { trilinearInterpolate } from '~/engine/util/algorithms/sdf/trilinearSample';

const CHUNK_SLICES = 4;

const IDENTITY: ReadonlyMat4 = mat4.create();

// Destination point to source point, in mm: pre, then an optional displacement, then post.
export interface VolumeMapping {
    readonly pre: ReadonlyMat4;
    readonly displacementInto?: (point: vec3, out: vec3) => boolean;
    readonly post: ReadonlyMat4;
}

export interface ResampleProgress {
    readonly done: number;
    readonly total: number;
}

export interface ResampleVolumeOptions {
    readonly backgroundValue?: number;
    readonly isCancelled?: () => boolean;
    readonly onProgress?: (progress: ResampleProgress) => void;
}

export interface ResampleCoverage {
    // Destination voxels the mapping does not cover: left at background.
    readonly unmappedVoxels: number;
    // Destination voxels that map outside the source grid: left at background.
    readonly outsideSourceVoxels: number;
}

export function rigidVolumeMapping(dstToSrc: ReadonlyMat4): VolumeMapping {
    assertAffine(dstToSrc, 'rigid');
    return { pre: dstToSrc, post: IDENTITY };
}

export function deformableVolumeMapping(
    transform: DeformableTransform
): VolumeMapping {
    assertAffine(transform.post, 'post');
    assertAffine(transform.pre, 'pre');
    return {
        pre: transform.pre,
        displacementInto: (point, out) =>
            dvfDisplacementInto(transform.field, point, out),
        post: transform.post,
    };
}

// Trilinear at destination voxel centers; an Int16 destination is rounded to nearest.
export async function resampleVolumeInto(
    src: Int16Array | Float32Array,
    srcGrid: Grid,
    dst: Int16Array | Float32Array,
    dstGrid: Grid,
    mapping: VolumeMapping,
    options: ResampleVolumeOptions = {}
): Promise<ResampleCoverage | null> {
    const srcNx = srcGrid.sizeX | 0;
    const srcNy = srcGrid.sizeY | 0;
    const srcNz = srcGrid.sizeZ | 0;
    if (src.length !== srcNx * srcNy * srcNz) {
        throw new Error('Source pixel data length does not match srcGrid.');
    }
    const dstNx = dstGrid.sizeX | 0;
    const dstNy = dstGrid.sizeY | 0;
    const dstNz = dstGrid.sizeZ | 0;
    if (dst.length !== dstNx * dstNy * dstNz) {
        throw new Error(
            'Destination pixel data length does not match dstGrid.'
        );
    }
    const background = options.backgroundValue ?? 0;
    const isCancelled = options.isCancelled ?? (() => false);
    const round = dst instanceof Int16Array;

    // A destination index names a voxel center; grids put index i at its low corner.
    const preFromDstVoxel = mat4.multiply(
        mat4.create(),
        mapping.pre,
        dstGrid.voxelToWorld
    );
    const qOrigin = vec3.transformMat4(
        vec3.create(),
        vec3.fromValues(0.5, 0.5, 0.5),
        preFromDstVoxel
    );
    const qStepX = columnStep(preFromDstVoxel, 0);
    const qStepY = columnStep(preFromDstVoxel, 1);
    const qStepZ = columnStep(preFromDstVoxel, 2);

    const srcVoxelFromQ = mat4.multiply(
        mat4.create(),
        srcGrid.worldToVoxel,
        mapping.post
    );

    dst.fill(background);
    const q = vec3.create();
    const displacement = vec3.create();
    const displaced = vec3.create();
    let unmappedVoxels = 0;
    let outsideSourceVoxels = 0;

    for (let kStart = 0; kStart < dstNz; kStart += CHUNK_SLICES) {
        // Yield first so the caller's progress can paint before any work runs.
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (isCancelled()) {
            return null;
        }
        const kEnd = Math.min(kStart + CHUNK_SLICES, dstNz);
        for (let k = kStart; k < kEnd; k++) {
            for (let j = 0; j < dstNy; j++) {
                let outIndex = (k * dstNy + j) * dstNx;
                q[0] = qOrigin[0] + j * qStepY[0] + k * qStepZ[0];
                q[1] = qOrigin[1] + j * qStepY[1] + k * qStepZ[1];
                q[2] = qOrigin[2] + j * qStepY[2] + k * qStepZ[2];
                for (let i = 0; i < dstNx; i++, outIndex++) {
                    let mapped = true;
                    if (mapping.displacementInto) {
                        mapped = mapping.displacementInto(q, displacement);
                        if (mapped) {
                            vec3.add(displaced, q, displacement);
                        }
                    } else {
                        vec3.copy(displaced, q);
                    }
                    if (mapped) {
                        vec3.transformMat4(displaced, displaced, srcVoxelFromQ);
                        const value = sampleSource(
                            src,
                            srcNx,
                            srcNy,
                            srcNz,
                            displaced[0] - 0.5,
                            displaced[1] - 0.5,
                            displaced[2] - 0.5
                        );
                        if (value === undefined) {
                            outsideSourceVoxels++;
                        } else {
                            dst[outIndex] = round ? Math.round(value) : value;
                        }
                    } else {
                        unmappedVoxels++;
                    }
                    q[0] += qStepX[0];
                    q[1] += qStepX[1];
                    q[2] += qStepX[2];
                }
            }
        }
        options.onProgress?.({ done: kEnd, total: dstNz });
    }

    return isCancelled() ? null : { unmappedVoxels, outsideSourceVoxels };
}

function columnStep(m: mat4, axis: 0 | 1 | 2): vec3 {
    const c = axis * 4;
    return vec3.fromValues(m[c], m[c + 1], m[c + 2]);
}

function assertAffine(m: ReadonlyMat4, name: string): void {
    if (m[3] !== 0 || m[7] !== 0 || m[11] !== 0 || m[15] !== 1) {
        throw new Error(
            `Registration ${name} matrix is not affine; a volume cannot be ` +
                'resampled through it.'
        );
    }
}

function sampleSource(
    pixels: Int16Array | Float32Array,
    nx: number,
    ny: number,
    nz: number,
    x: number,
    y: number,
    z: number
): number | undefined {
    if (
        x < -0.5 ||
        y < -0.5 ||
        z < -0.5 ||
        x > nx - 0.5 ||
        y > ny - 0.5 ||
        z > nz - 0.5
    ) {
        return undefined;
    }
    // Clamped before the floor: within half a voxel of an edge the edge voxel's value holds, on both sides.
    const cx = Math.min(Math.max(x, 0), nx - 1);
    const cy = Math.min(Math.max(y, 0), ny - 1);
    const cz = Math.min(Math.max(z, 0), nz - 1);
    const x0 = Math.floor(cx);
    const y0 = Math.floor(cy);
    const z0 = Math.floor(cz);
    const tx = cx - x0;
    const ty = cy - y0;
    const tz = cz - z0;
    const x1 = Math.min(x0 + 1, nx - 1);
    const y1 = Math.min(y0 + 1, ny - 1);
    const z1 = Math.min(z0 + 1, nz - 1);
    const rowY0 = y0 * nx;
    const rowY1 = y1 * nx;
    const sliceZ0 = z0 * nx * ny;
    const sliceZ1 = z1 * nx * ny;

    return trilinearInterpolate(
        pixels[sliceZ0 + rowY0 + x0],
        pixels[sliceZ0 + rowY0 + x1],
        pixels[sliceZ0 + rowY1 + x0],
        pixels[sliceZ0 + rowY1 + x1],
        pixels[sliceZ1 + rowY0 + x0],
        pixels[sliceZ1 + rowY0 + x1],
        pixels[sliceZ1 + rowY1 + x0],
        pixels[sliceZ1 + rowY1 + x1],
        tx,
        ty,
        tz
    );
}
